// TraitementConges.jsx (RH/Admin) — Redesigné avec le style TraitementAvances

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    AlertCircle,
    FileText,
    ChevronDown,
    CalendarDays,
    Loader,
    Eye,
    Download,
    RotateCcw,
} from "lucide-react";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../../../../api";
import {
    getAdminInboxRequest,
    adminDecisionRequest,
    clearLeaveRequestMessage,
    exportLeaveRequestsExcelRequest,
    adjustLeaveRequestPeriodRequest,
} from "../../../../redux/actions/leaveRequestsActions";
import {
    getWorkdaysConfigRequest,
    getCalendarSettingsRequest,
} from "../../../../redux/actions/workDaysActions";
import "../TraitementStyles/TraitementNotesFrais.css";
import "../TraitementStyles/TreatmentMesOrdres.css";
import { getRequestTreatedBy } from "./treatmentDecisionInfo";

const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    useEffect(() => { setQuery(value || ""); }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => { setQuery(opt.label); onChange(opt.value); setOpen(false); };
    const handleInputChange = (e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); };
    const handleClear = () => { setQuery(""); onChange(""); setOpen(false); };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="dcInput"
                    style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Effacer">
                            <X size={13} />
                        </button>
                    )}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Afficher la liste">
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    Aucun résultat
                </div>
            )}
        </div>
    );
};

const TraitementConges = () => {
    const dispatch = useDispatch();

    // Redux state
    const leaveState = useSelector((state) => state.leaveRequests || {});
    const { workdaysConfig, calendarSettings } = useSelector((state) => state.workDays || {});
    const loading = !!leaveState.loading;
    const apiError = leaveState.error;
    const inbox = leaveState.adminInbox || [];

    const [activeTab, setActiveTab] = useState("enCours");

    // Filtres
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDateDebut, setFilterDateDebut] = useState("");
    const [filterDateFin, setFilterDateFin] = useState("");

    const handleResetFilters = () => {
        setFilterCollab("");
        setFilterType("");
        setFilterDateDebut("");
        setFilterDateFin("");
    };

    const [showDecisionModal, setShowDecisionModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [decisionComment, setDecisionComment] = useState("");
    const [pendingDecision, setPendingDecision] = useState(null); // "approve" | "reject"
    const [previewDoc, setPreviewDoc] = useState(null); // { url, name, path }
    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [adjustStartDate, setAdjustStartDate] = useState("");
    const [adjustEndDate, setAdjustEndDate] = useState("");
    const [adjustDateReprise, setAdjustDateReprise] = useState("");

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAdminInboxRequest());
        dispatch(getWorkdaysConfigRequest());
        dispatch(getCalendarSettingsRequest(new Date().getFullYear()));
    }, [dispatch]);

    // Synchronise automatiquement la liste RH avec les nouvelles demandes
    // sans solliciter l'API lorsque l'onglet n'est pas visible.
    useEffect(() => {
        const refreshInbox = () => {
            if (document.visibilityState === "visible") {
                dispatch(getAdminInboxRequest({ silent: true }));
            }
        };

        const intervalId = window.setInterval(refreshInbox, 5_000);
        document.addEventListener("visibilitychange", refreshInbox);
        window.addEventListener("focus", refreshInbox);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", refreshInbox);
            window.removeEventListener("focus", refreshInbox);
        };
    }, [dispatch]);

    const fixedHolidays = calendarSettings?.fixed || [];
    const hijriHolidays = calendarSettings?.hijri || [];
    const customHolidays = calendarSettings?.custom || [];

    const checkIsHoliday = (d) => {
        if (!d) return false;
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const day = d.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        const isFixed = (fixedHolidays || []).some((h) => h.date === dateStr && h.selected);
        if (isFixed) return true;

        const isHijri = (hijriHolidays || []).some((h) => {
            if (!h.selected || !h.date) return false;
            const holidayDate = new Date(h.date);
            const checkDate = new Date(d);
            holidayDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const testDate = new Date(holidayDate);
                testDate.setDate(testDate.getDate() + i);
                testDate.setHours(0, 0, 0, 0);

                if (testDate.getTime() === checkDate.getTime()) return true;
            }
            return false;
        });
        if (isHijri) return true;

        const fullDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return (customHolidays || []).some((h) => h.date === fullDate);
    };

    const getProchainJourOuvre = (dateStr) => {
        if (!dateStr) return "";
        let d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "";

        d.setDate(d.getDate() + 1);

        let safetyCount = 0;
        while (safetyCount < 30) {
            const dayIndex = d.getDay();
            const dayName = dayIndex === 0 ? "dimanche" : 
                            dayIndex === 1 ? "lundi" : 
                            dayIndex === 2 ? "mardi" : 
                            dayIndex === 3 ? "mercredi" : 
                            dayIndex === 4 ? "jeudi" : 
                            dayIndex === 5 ? "vendredi" : "samedi";

            let isWorking = true;
            if (workdaysConfig && workdaysConfig.workDays && workdaysConfig.workDays[dayName]) {
                const cfg = workdaysConfig.workDays[dayName];
                isWorking = typeof cfg.isWorking === "boolean" ? cfg.isWorking : 
                            typeof cfg.working === "boolean" ? cfg.working : true;
            } else {
                isWorking = dayIndex !== 0 && dayIndex !== 6;
            }

            if (isWorking && !checkIsHoliday(d)) {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                const dayVal = String(d.getDate()).padStart(2, "0");
                return `${y}-${m}-${dayVal}`;
            }
            d.setDate(d.getDate() + 1);
            safetyCount++;
        }
        return "";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const isPostponementRejected = (d) =>
        d.originalDateDebut &&
        String(d.rawStatus || d.status || "").toUpperCase() === "APPROVED" &&
        (d.adminDecision === false || (d.managerDecision === false && d.adminDecision == null));

    const isRejectedByAdmin = (d) =>
        d?.adminDecision === false &&
        (String(d.rawStatus || d.status || "").toUpperCase() === "REJECTED" || isPostponementRejected(d));

    const mapStatusToTab = (d) => {
        const v = String(d.status || "").toUpperCase();
        if (v === "PENDING_ADMIN") return "enCours";
        if (v === "POSTPONED") return "approuve";
        if (v === "APPROVED") {
            if (isPostponementRejected(d)) return "rejete";
            return "approuve";
        }
        if (v === "REJECTED") return "rejete";
        if (v === "CANCELLED") return "annule";
        return null;
    };

    const demandes = useMemo(() => {
        return (inbox || [])
            .map((d) => {
                const tab = mapStatusToTab(d);
                if (!tab) return null;
                return {
                    id: d.id,
                    matricule: d.employeeCode || "—",
                    nom: d.employeeName || "—",
                    typeConge: d.absenceTypeNom || "—",
                    dateDebut: d.dateDebut,
                    dateFin: d.dateFin,
                    dateReprise: d.dateReprise || null,
                    duree: d.duree ?? 0,
                    motif: d.commentaire || " - ",
                    justificatif: d.justificatif || null,
                    statut: tab,
                    rawStatus: d.status,
                    rejectedByAdmin: isRejectedByAdmin(d),
                    managerDecision: d.managerDecision ?? null,
                    managerName: d.managerName || null,
                    managerDecisionAt: d.managerDecisionAt || null,
                    managerComment: d.managerComment || null,
                    adminDecision: d.adminDecision ?? null,
                    adminName: d.adminName || null,
                    adminDecisionAt: d.adminDecisionAt || null,
                    adminComment: d.adminComment || null,
                    originalDateDebut: d.originalDateDebut,
                    originalDateFin: d.originalDateFin,
                    dateDemande: d.createdAt,
                    justificatifs: d.justificatifs || [],
                };
            })
            .filter(Boolean);
    }, [inbox]);

    const getStatutBadge = (demande) => {
        const raw = String(demande.rawStatus || "").toUpperCase();
        if (raw === "POSTPONEMENT_REQUEST" || raw === "POSTPONED_REQUEST") {
            return (
                <span className="tcBadge" style={{ backgroundColor: "#f0f9ff", color: "#0c4a6e" }}>
                    En attente de report
                </span>
            );
        }
        if (raw === "POSTPONED") {
            return (
                <span className="tcBadge" style={{ backgroundColor: "#d4edda", color: "#155724", display: "flex", alignItems: "center", gap: 4 }}>
                    Approuvée
                    <span style={{ background: "#0369a1", color: "#fff", borderRadius: 4, fontSize: 10, padding: "1px 5px", fontWeight: 700 }}>Après report</span>
                </span>
            );
        }
        if (isPostponementRejected(demande)) {
            return (
                <span className="tcBadge" style={{ backgroundColor: "#f8d7da", color: "#721c24", display: "flex", alignItems: "center", gap: 4 }}>
                    Rejetée
                    <span style={{ background: "#dc2626", color: "#fff", borderRadius: 4, fontSize: 10, padding: "1px 5px", fontWeight: 700 }}>Après report</span>
                </span>
            );
        }
        const config = {
            enCours: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            approuve: { label: "Approuvée", bg: "#d4edda", color: "#155724" },
            rejete: { label: "Rejetée", bg: "#f8d7da", color: "#721c24" },
            annule: { label: "Annulée", bg: "#f3f4f6", color: "#6b7280" },
        };
        const c = config[demande.statut] || config.enCours;
        return (
            <span className="tcBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const collaborateurOptions = useMemo(() => {
        const map = new Map();
        demandes.forEach((d) => {
            if (d.nom && !map.has(d.nom)) {
                map.set(d.nom, { value: d.nom, label: d.matricule ? `${d.nom} (${d.matricule})` : d.nom });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [demandes]);

    const typeCongeOptions = useMemo(() => {
        return [...new Set(demandes.map((d) => d.typeConge).filter(Boolean))]
            .sort()
            .map((t) => ({ value: t, label: t }));
    }, [demandes]);

    const getFilteredDemandes = () => {
        let filtered = demandes.filter((d) =>
            activeTab === "rejete"
                ? d.rejectedByAdmin
                : d.statut === activeTab
        );

        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            filtered = filtered.filter(
                (d) =>
                    (d.nom && d.nom.toLowerCase().includes(val)) ||
                    (d.matricule && d.matricule.toLowerCase().includes(val))
            );
        }
        if (filterType) {
            const val = filterType.toLowerCase();
            filtered = filtered.filter((d) => (d.typeConge || "").toLowerCase().includes(val));
        }
        if (filterDateDebut) {
            filtered = filtered.filter((d) => {
                if (!d.dateDebut) return false;
                return new Date(d.dateDebut).toISOString().split("T")[0] === filterDateDebut;
            });
        }
        if (filterDateFin) {
            filtered = filtered.filter((d) => {
                if (!d.dateFin) return false;
                return new Date(d.dateFin).toISOString().split("T")[0] === filterDateFin;
            });
        }
        return filtered;
    };

    const tabs = useMemo(() => {
        return [
            {
                id: "enCours",
                label: "En cours",
                count: demandes.filter((d) => d.statut === "enCours").length,
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: demandes.filter((d) => d.statut === "approuve").length,
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: demandes.filter((d) => d.rejectedByAdmin).length,
            },
            {
                id: "annule",
                label: "Annulées",
                count: demandes.filter((d) => d.statut === "annule").length,
            },
        ];
    }, [demandes]);

    const handleOpenDecision = (demande, decision) => {
        if (decision === "approve") {
            Swal.fire({
                title: "Confirmation",
                text: "Êtes-vous sûr de vouloir approuver cette demande de congé ?",
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#10b981",
                cancelButtonColor: "#6b7280",
                confirmButtonText: "Oui, approuver",
                cancelButtonText: "Annuler",
            }).then((result) => {
                if (result.isConfirmed) {
                    dispatch(
                        adminDecisionRequest({
                            id: demande.id,
                            approved: true,
                            comment: "",
                        })
                    );
                    dispatch(clearLeaveRequestMessage());
                }
            });
            return;
        }

        setSelectedDemande(demande);
        setPendingDecision(decision);
        setDecisionComment("");
        setShowDecisionModal(true);
    };

    const confirmDecision = () => {
        if (!selectedDemande) return;
        const approved = pendingDecision === "approve";
        if (!approved && !decisionComment.trim()) return;

        dispatch(
            adminDecisionRequest({
                id: selectedDemande.id,
                approved,
                comment: decisionComment || "",
            })
        );
        dispatch(clearLeaveRequestMessage());
        closeDecision();
    };

    const closeDecision = () => {
        setShowDecisionModal(false);
        setDecisionComment("");
        setSelectedDemande(null);
        setPendingDecision(null);
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const handleOpenAdjust = (demande) => {
        setSelectedDemande(demande);
        setAdjustStartDate(demande.dateDebut);
        setAdjustEndDate(demande.dateFin);
        setAdjustDateReprise(demande.dateFin ? getProchainJourOuvre(demande.dateFin) : (demande.dateReprise || ""));
        setShowAdjustModal(true);
    };

    const confirmAdjust = () => {
        if (!selectedDemande || !adjustStartDate || !adjustEndDate) return;
        dispatch(
            adjustLeaveRequestPeriodRequest({
                id: selectedDemande.id,
                startDate: adjustStartDate,
                endDate: adjustEndDate,
                dateReprise: adjustDateReprise,
            })
        );
        closeAdjust();
    };

    const closeAdjust = () => {
        setShowAdjustModal(false);
        setSelectedDemande(null);
        setAdjustStartDate("");
        setAdjustEndDate("");
        setAdjustDateReprise("");
    };

    const handlePreview = (path) => {
        if (!path) return;
        const url = `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path)}&inline=true`;
        setPreviewDoc({
            url,
            name: path.split("/").pop(),
            path: path
        });
    };

    const closePreview = () => setPreviewDoc(null);

    const handleDownload = (path) => {
        if (!path) return;
        const url = `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path)}`;
        window.open(url, "_blank");
    };

    const safeErrorText = (err) => {
        if (!err) return "";
        if (typeof err === "string") return err;
        if (err?.message) return String(err.message);
        try { return JSON.stringify(err); } catch { return String(err); }
    };

    const filtered = getFilteredDemandes();

    if (loading && demandes.length === 0) {
        return (
            <div className="tcPage">
                <div className="tcWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                <div className="tnfHeader">
                    <div>
                        <h1>Traitement des demandes de congés</h1>
                        <p>Gérez et validez les demandes de congés et absences de vos collaborateurs</p>
                    </div>
                </div>

                <div className="tnfCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Collaborateur / Matricule
                            </label>
                            <SearchableCombobox value={filterCollab} onChange={setFilterCollab} options={collaborateurOptions} placeholder="Nom ou matricule..." />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Type de congé
                            </label>
                            <SearchableCombobox value={filterType} onChange={setFilterType} options={typeCongeOptions} placeholder="Rechercher un type..." />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Date début
                            </label>
                            <input type="date" value={filterDateDebut} onChange={(e) => setFilterDateDebut(e.target.value)} className="dcInput" style={{ width: "100%", fontSize: "13px" }} />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Date fin
                            </label>
                            <input type="date" value={filterDateFin} onChange={(e) => setFilterDateFin(e.target.value)} className="dcInput" style={{ width: "100%", fontSize: "13px" }} />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" onClick={handleResetFilters} className="dcBtn dcBtn--outline" style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}>
                                <RotateCcw size={14} /> Réinitialiser
                            </button>
                            <button type="button" className="dcBtn dcBtn--outline treatmentExportBtn" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", background: "#10b981", color: "white", border: "none", whiteSpace: "nowrap" }} onClick={() => dispatch(exportLeaveRequestsExcelRequest("admin_inbox"))}>
                                <Download size={14} /> Exporter Excel
                            </button>
                        </div>
                    </div>
                </div>

                {apiError && <div style={{ marginTop: 8, color: "#ef4444", fontSize: 12 }}>{safeErrorText(apiError)}</div>}

                <div className="tnfCard tnfCard--noPad">
                    <div className="tnfTabs">
                        {tabs.map((tab) => (
                            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`tnfTab ${activeTab === tab.id ? "is-active" : ""}`}>
                                {tab.label}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tnfTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tnfEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Type</th>
                                    <th>Période</th>
                                    <th>Durée</th>
                                    {(activeTab === "approuve" || activeTab === "rejete") && <th>Traitée par</th>}
                                    {activeTab === "rejete" && <th>Motif</th>}
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr key={demande.id} onClick={() => showDetails(demande)} style={{ cursor: "pointer" }}>
                                        <td><span className="tnfPrimary">{demande.matricule}</span></td>
                                        <td><span className="tnfPrimary">{demande.nom}</span></td>
                                        <td>
                                            <div className="tnfType">
                                                <span className="tnfDot" style={{ backgroundColor: "#3b82f6" }} />
                                                <span className="tnfPrimary">{demande.typeConge}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="tnfPrimary">{formatDate(demande.dateDebut)}</div>
                                            <div className="tnfSub">→ {formatDate(demande.dateFin)}</div>
                                            {demande.dateReprise && (
                                                <div style={{ fontSize: 11, color: "#059669", marginTop: 2, fontWeight: 500 }}>
                                                    Reprise : {formatDate(demande.dateReprise)}
                                                </div>
                                            )}
                                            {demande.originalDateDebut && (
                                                <div style={{ fontSize: 10, color: "#64748b", marginTop: 3 }}>
                                                    <span style={{ textDecoration: "line-through" }}>
                                                        {formatDate(demande.originalDateDebut)} → {formatDate(demande.originalDateFin)}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td><span className="tnfPrimary">{demande.duree} jour{demande.duree > 1 ? "s" : ""}</span></td>
                                        {(activeTab === "approuve" || activeTab === "rejete") && (
                                            <td><span className="tnfMuted">{getRequestTreatedBy(demande)}</span></td>
                                        )}

                                        {activeTab === "rejete" && (
                                            <td>
                                                <span className="tnfMuted" style={{ maxWidth: 160, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {demande.adminComment || demande.managerComment || demande.motif || "—"}
                                                </span>
                                            </td>
                                        )}

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="tnfRowActions">
                                                {activeTab === "enCours" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="tnfActionBtn tnfActionBtn--ok"
                                                            onClick={() => handleOpenDecision(demande, "approve")}
                                                            title="Approuver"
                                                            disabled={loading}
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tnfActionBtn tnfActionBtn--no"
                                                            onClick={() => handleOpenDecision(demande, "reject")}
                                                            title="Refuser"
                                                            disabled={loading}
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </>
                                                )}
                                                {activeTab === "approuve" && (
                                                    <button
                                                        type="button"
                                                        className="tnfActionBtn"
                                                        onClick={() => handleOpenAdjust(demande)}
                                                        title="Retour anticipé (Ajuster la période)"
                                                        style={{ background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }}
                                                    >
                                                        <CalendarDays size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="tnfActionBtn"
                                                    onClick={() => showDetails(demande)}
                                                    title="Voir les détails"
                                                    style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon">
                                    <CalendarDays size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">Détails de la demande #{selectedDemande.id}</div>
                                    <div className="tnfModal__sub">Demande du {formatDate(selectedDemande.dateDebut)}</div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            {selectedDemande.originalDateDebut && selectedDemande.rawStatus === "APPROVED" && (
                                <div className="tnfPanel tnfPanel--full" style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#d97706", fontWeight: "bold", fontSize: 14 }}>
                                        <AlertCircle size={18} />
                                        Congé Ajusté (Retour Anticipé)
                                    </div>
                                    <div style={{ marginTop: 6, fontSize: 12, color: "#b45309", lineHeight: 1.5 }}>
                                        Ce congé a été raccourci par l'administration RH.
                                        La période planifiée initialement était du <b>{formatDate(selectedDemande.originalDateDebut)}</b> au <b>{formatDate(selectedDemande.originalDateFin)}</b>.
                                        Les jours non consommés ont été restitués au solde de l'employé.
                                    </div>
                                </div>
                            )}
                            <div className="tnfGrid2">
                                {/* Infos collaborateur */}
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <User size={16} /> Informations collaborateur
                                    </div>
                                    <div className="tnfKv">
                                        <span>Matricule</span>
                                        <b>{selectedDemande.matricule}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Nom</span>
                                        <b>{selectedDemande.nom}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>Date de la demande</span>
                                        <b>{formatDate(selectedDemande.dateDemande)}</b>
                                    </div>
                                </div>

                                {/* Détails congé */}
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">
                                        <CalendarDays size={16} /> Détails du congé
                                    </div>
                                    <div className="tnfKv">
                                        <span>Type</span>
                                        <b>{selectedDemande.typeConge}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{selectedDemande.originalDateDebut ? "Nouvelle Date début" : "Date de début"}</span>
                                        <b className="tnfAccent">{formatDate(selectedDemande.dateDebut)}</b>
                                    </div>
                                    <div className="tnfKv">
                                        <span>{selectedDemande.originalDateFin ? "Nouvelle Date fin" : "Date de fin"}</span>
                                        <b className="tnfAccent">{formatDate(selectedDemande.dateFin)}</b>
                                    </div>
                                    {selectedDemande.dateReprise && (
                                        <div className="tnfKv">
                                            <span>Date de reprise</span>
                                            <b style={{ color: "#059669" }}>{formatDate(selectedDemande.dateReprise)}</b>
                                        </div>
                                    )}
                                    {selectedDemande.originalDateDebut && (
                                        <div className="tnfKv" style={{ background: "#fffbeb", padding: "4px 8px", borderRadius: 6, marginTop: 4 }}>
                                            <span style={{ color: "#92400e" }}>Ancienne période</span>
                                            <b style={{ color: "#92400e", textDecoration: "line-through" }}>
                                                {formatDate(selectedDemande.originalDateDebut)} → {formatDate(selectedDemande.originalDateFin)}
                                            </b>
                                        </div>
                                    )}
                                    <div className="tnfKv">
                                        <span>Durée</span>
                                        <b>{selectedDemande.duree} jour{selectedDemande.duree > 1 ? "s" : ""}</b>
                                    </div>
                                </div>

                                {/* Motif */}
                                <div className="tnfPanel tnfPanel--full">
                                    <div className="tnfPanel__title">
                                        <FileText size={16} /> Motif de la demande
                                    </div>
                                    <div className="tnfText">{selectedDemande.motif}</div>
                                </div>

                                {/* Justificatifs */}
                                {(selectedDemande.justificatif || (selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0)) && (
                                    <div className="tnfPanel tnfPanel--full">
                                        <div className="tnfPanel__title">
                                            <FileText size={16} /> Justificatifs fournis
                                        </div>
                                        <div className="tnfDocs" style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "10px" }}>
                                            {/* Support de l'ancien champ (string) */}
                                            {selectedDemande.justificatif && !selectedDemande.justificatifs?.includes(selectedDemande.justificatif) && (
                                                <div className="tnfDoc" onClick={() => handlePreview(selectedDemande.justificatif)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", transition: "all 0.2s" }}>
                                                    <FileText size={18} color="#64748b" />
                                                    <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#334155" }}>
                                                        {selectedDemande.justificatif.split("/").pop()}
                                                    </div>
                                                    <Eye size={16} color="#3b82f6" />
                                                </div>
                                            )}
                                            {/* Support du nouveau champ (liste) */}
                                            {selectedDemande.justificatifs?.map((j, idx) => (
                                                <div key={idx} className="tnfDoc" onClick={() => handlePreview(j)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", transition: "all 0.2s" }}>
                                                    <FileText size={18} color="#64748b" />
                                                    <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#334155" }}>
                                                        {j.split("/").pop()}
                                                    </div>
                                                    <Eye size={16} color="#3b82f6" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Décision manager */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className="tnfPanel tnfPanel--full">
                                        <div className="tnfPanel__title">
                                            <Check size={16} /> Validation Manager
                                        </div>
                                        <div className="tnfKv">
                                            <span>Décision</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && (
                                            <div className="tnfKv">
                                                <span>Par</span>
                                                <b>{selectedDemande.managerName}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="tnfKv">
                                                <span>Date</span>
                                                <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="tnfRejectBox">
                                                <div className="tnfRejectLabel">Commentaire</div>
                                                <div className="tnfRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Validation RH */}
                                {/*{selectedDemande.statut !== "enCours" && selectedDemande.adminDecision !== null && (*/}
                                {/*    <div className={`tnfPanel tnfPanel--full ${selectedDemande.statut === "approuve" ? "tnfValidOk" : "tnfValidNo"}`}>*/}
                                {/*        <div className="tnfPanel__title">*/}
                                {/*            {selectedDemande.statut === "approuve" ? <Check size={16} /> : <X size={16} />}*/}
                                {/*            {selectedDemande.statut === "approuve" ? " Demande approuvée" : " Demande rejetée"}*/}
                                {/*        </div>*/}
                                {/*        <div className="tnfKv">*/}
                                {/*            <span>Date de validation</span>*/}
                                {/*            <b>{formatDate(selectedDemande.adminDecisionAt)}</b>*/}
                                {/*        </div>*/}
                                {/*        {selectedDemande.adminName && (*/}
                                {/*            <div className="tnfKv">*/}
                                {/*                <span>Validé par</span>*/}
                                {/*                <b>{selectedDemande.adminName}</b>*/}
                                {/*            </div>*/}
                                {/*        )}*/}
                                {/*        {selectedDemande.adminComment && (*/}
                                {/*            <div className="tnfRejectBox">*/}
                                {/*                <div className="tnfRejectLabel">*/}
                                {/*                    {selectedDemande.statut === "approuve" ? "Commentaire" : "Motif du rejet"}*/}
                                {/*                </div>*/}
                                {/*                <div className="tnfRejectText">{selectedDemande.adminComment}</div>*/}
                                {/*            </div>*/}
                                {/*        )}*/}
                                {/*    </div>*/}
                                {/*)}*/}
                            </div>
                        </div>

                        {/* Actions footer */}
                        <div className="tnfModal__footer">
                            {selectedDemande.statut === "enCours" ? (
                                <>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--danger"
                                        onClick={() => {
                                            closeDetails();
                                            handleOpenDecision(selectedDemande, "reject");
                                        }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ok"
                                        onClick={() => {
                                            closeDetails();
                                            handleOpenDecision(selectedDemande, "approve");
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>
                                    <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* DECISION MODAL (Approuver ou Refuser) */}
            {showDecisionModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className={`tnfModal__icon ${pendingDecision === "reject" ? "tnfModal__icon--danger" : ""}`}>
                                    {pendingDecision === "approve" ? <Check size={18} /> : <AlertCircle size={18} />}
                                </div>
                                <div>
                                    <div className="tnfModal__title">
                                        {pendingDecision === "approve" ? "Approuver la demande de congé" : "Refuser la demande de congé"}
                                    </div>
                                    <div className="tnfModal__sub">
                                        {selectedDemande.nom} — {selectedDemande.typeConge}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDecision} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfMiniCard">
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Période</span>
                                    <b>{formatDate(selectedDemande.dateDebut)} → {formatDate(selectedDemande.dateFin)}</b>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Durée</span>
                                    <b className="tnfAccent">{selectedDemande.duree} jour{selectedDemande.duree > 1 ? "s" : ""}</b>
                                </div>
                            </div>

                            <label className="tnfLabel">
                                {pendingDecision === "reject" ? (
                                    <>Motif du refus <span className="tnfReq">*</span></>
                                ) : (
                                    <>Commentaire <span className="tnfMuted">(optionnel)</span></>
                                )}
                            </label>
                            <textarea
                                value={decisionComment}
                                required
                                onChange={(e) => setDecisionComment(e.target.value)}
                                rows={5}
                                placeholder={
                                    pendingDecision === "reject"
                                        ? "Veuillez préciser le motif du refus de cette demande..."
                                        : "Ajouter un commentaire (optionnel)..."
                                }
                                className="tnfTextarea"
                            />
                            <div className="tnfHelp">Ce commentaire sera communiqué au collaborateur</div>
                        </div>

                        <div className="tnfModal__footer">
                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDecision}>
                                Annuler
                            </button>

                            {pendingDecision === "approve" ? (
                                <button
                                    type="button"
                                    className="tnfBtn tnfBtn--ok"
                                    onClick={confirmDecision}
                                    disabled={loading}
                                >
                                    {loading ? <Loader size={16} className="spin" /> : "Confirmer l'approbation"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="tnfBtn tnfBtn--danger"
                                    onClick={confirmDecision}
                                    disabled={!decisionComment.trim() || loading}
                                >
                                    {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* PREVIEW MODAL */}
            {previewDoc && (
                <div className="tnfModal" style={{ zIndex: 10001 }}>
                    <div className="tnfModal__panel tnfModal__panel--wide" style={{ maxWidth: "90vw", width: "1000px" }}>
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon"><FileText size={18} /></div>
                                <div>
                                    <div className="tnfModal__title">Aperçu du document</div>
                                    <div className="tnfModal__sub">{previewDoc.name}</div>
                                </div>
                            </div>
                            <button type="button" className="tnfIconBtn" onClick={closePreview}><X size={18} /></button>
                        </div>
                        <div className="tnfModal__body" style={{ height: "75vh", padding: 0, overflow: "hidden", background: "#f1f5f9", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            {previewDoc.name.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                                    <img src={previewDoc.url} alt="Aperçu" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 4, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)" }} />
                                </div>
                            ) : previewDoc.name.match(/\.pdf$/i) ? (
                                <iframe src={previewDoc.url} title="Aperçu PDF" width="100%" height="100%" style={{ border: "none" }} />
                            ) : (
                                <div style={{ textAlign: "center", padding: 40 }}>
                                    <AlertCircle size={48} color="#64748b" style={{ marginBottom: 16 }} />
                                    <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 8 }}>Aperçu non disponible</div>
                                    <p style={{ color: "#64748b", marginBottom: 24 }}>Ce type de fichier ne peut pas être prévisualisé directement.</p>
                                    <button className="tnfBtn tnfBtn--primary" onClick={() => handleDownload(previewDoc.path)}>
                                        <Download size={16} /> Télécharger pour consulter
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="tnfModal__footer">
                            <button className="tnfBtn tnfBtn--ghost" onClick={closePreview}>Fermer</button>
                            <button className="tnfBtn tnfBtn--primary" onClick={() => handleDownload(previewDoc.path)}>
                                <Download size={16} /> Télécharger le document
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ADJUST MODAL (Retour anticipé / Modification de période) */}
            {showAdjustModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon" style={{ background: "#fffbeb", color: "#d97706" }}>
                                    <CalendarDays size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">Retour Anticipé / Ajuster le congé</div>
                                    <div className="tnfModal__sub">
                                        {selectedDemande.nom} — {selectedDemande.typeConge}
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="tnfIconBtn" onClick={closeAdjust} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfMiniCard" style={{ marginBottom: 16 }}>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Période initiale</span>
                                    <b>{formatDate(selectedDemande.dateDebut)} → {formatDate(selectedDemande.dateFin)}</b>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">Durée initiale</span>
                                    <b className="tnfAccent">{selectedDemande.duree} jour{selectedDemande.duree > 1 ? "s" : ""}</b>
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                                <div className="tnfField">
                                    <label className="tnfLabel">Nouvelle date de début <span className="tnfReq">*</span></label>
                                    <input
                                        type="date"
                                        value={adjustStartDate}
                                        onChange={(e) => setAdjustStartDate(e.target.value)}
                                        className="tnfInput"
                                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box" }}
                                    />
                                </div>

                                <div className="tnfField">
                                    <label className="tnfLabel">Nouvelle date de fin <span className="tnfReq">*</span></label>
                                    <input
                                        type="date"
                                        value={adjustEndDate}
                                        onChange={(e) => {
                                            const newEnd = e.target.value;
                                            setAdjustEndDate(newEnd);
                                            setAdjustDateReprise(newEnd ? getProchainJourOuvre(newEnd) : "");
                                        }}
                                        className="tnfInput"
                                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box" }}
                                    />
                                </div>
                            </div>

                            <div className="tnfField" style={{ marginBottom: 16 }}>
                                <label className="tnfLabel">Date de reprise du travail (automatique)</label>
                                <input
                                    type="text"
                                    readOnly
                                    value={adjustDateReprise ? formatDate(adjustDateReprise) : "—"}
                                    className="tnfInput"
                                    style={{
                                        width: "100%",
                                        padding: "8px 12px",
                                        border: "1px solid #cbd5e1",
                                        borderRadius: 6,
                                        boxSizing: "border-box",
                                        backgroundColor: "#f8fafc",
                                        color: "#059669",
                                        fontWeight: 600,
                                        cursor: "default"
                                    }}
                                />
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                                    Suit automatiquement la date de fin (premier jour ouvré suivant, hors week-ends et jours fériés).
                                </div>
                            </div>

                            {/* Dynamically calculate & show refund */}
                            {(() => {
                                if (!adjustStartDate || !adjustEndDate) return null;
                                const start = new Date(adjustStartDate);
                                const end = new Date(adjustEndDate);
                                if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

                                const diffTime = end.getTime() - start.getTime();
                                const newDur = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;

                                if (newDur <= 0) {
                                    return (
                                        <div style={{ color: "#ef4444", fontSize: 13, fontWeight: "500", marginTop: 8 }}>
                                            ⚠️ La date de fin doit être supérieure ou égale à la date de début.
                                        </div>
                                    );
                                }

                                if (newDur > selectedDemande.duree) {
                                    return (
                                        <div style={{ color: "#ef4444", fontSize: 13, fontWeight: "500", marginTop: 8 }}>
                                            ⚠️ La nouvelle durée ({newDur} j) ne peut pas dépasser la durée initiale ({selectedDemande.duree} j).
                                        </div>
                                    );
                                }

                                const refund = selectedDemande.duree - newDur;
                                return (
                                    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 12, marginTop: 12 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                                            <span style={{ color: "#166534" }}>Nouvelle durée :</span>
                                            <b style={{ color: "#166534" }}>{newDur} jour{newDur > 1 ? "s" : ""}</b>
                                        </div>
                                        {refund > 0 ? (
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: "bold" }}>
                                                <span style={{ color: "#15803d" }}>Jours à restituer au solde :</span>
                                                <span style={{ color: "#15803d" }}>+{refund} jour{refund > 1 ? "s" : ""}</span>
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: 12, color: "#166534", fontStyle: "italic" }}>
                                                Aucune modification de durée (les dates sont identiques).
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="tnfModal__footer">
                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeAdjust}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tnfBtn tnfBtn--ok"
                                onClick={confirmAdjust}
                                disabled={
                                    loading ||
                                    !adjustStartDate ||
                                    !adjustEndDate ||
                                    (Math.round((new Date(adjustEndDate) - new Date(adjustStartDate)) / (1000 * 60 * 60 * 24)) + 1) <= 0 ||
                                    (Math.round((new Date(adjustEndDate) - new Date(adjustStartDate)) / (1000 * 60 * 60 * 24)) + 1) > selectedDemande.duree
                                }
                                style={{ background: "#d97706", borderColor: "#d97706" }}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer l'ajustement"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementConges;
